<template>
  <div class="f-v f-ast">
    <donut-modal-sections-title :title="title" />
    <div class="f">
      <div class="w-x11 t">Name: </div>
      <donut-input-text
        v-model="name"
        placeholder="New York Office, Engineering Team, etc."
        :max-length="30"
        class="flexed"
      />
    </div>
    <div class="f m-tx1">
      <div class="w-x11">
        <div class="t">Description: </div>
        <div class="t-sm gray">(Optional)</div>
      </div>
      <donut-input-text
        v-model="description"
        placeholder="Short description of your folder (Optional)"
        :max-length="60"
        class="flexed"
      />
    </div>
    <div class="f m-tx1">
      <div class="w-x11 t">Color: </div>
      <div class="flexed f f-gap-8">
        <div v-for="color in colors" :key="color">
          <div
            :class="`color-selector ${color === selectedColor ? 'selected' : ''}`"
            :style="styleForColor(color).main"
            @click="() => selectedColor = color"
          >
            <div class="color-selector-interior" :style="styleForColor(color).light" />
          </div>
        </div>
      </div>
    </div>
    <div class="f-as m-tx1">
      <div class="w-x11 t m-tx1">Journeys: </div>
      <donut-selector-multi-static
        v-model="tagsSelection"
        :options="availableTags"
        :key-map="{ value: 'id', label: 'name' }"
        placeholder="Pick at least one Journey"
        :allow-search="true"
        class="flexed"
      />
    </div>
    <donut-modal-sections-actions
      :is-save-disabled="saveDisabled"
      :loading-on-save="true"
      save-action="onboardingTagsOverview/submitFolderEditModal"
      :save-args="saveArgs"
      :save-tracking="{ eventName: 'Journey Folders - Edit Modal Submitted' }"
    >
      <template #actionsLeft>
        <donut-button v-if="folder" class="red inv" @click="openDeletionModal">Delete</donut-button>
      </template>
    </donut-modal-sections-actions>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useStore } from 'vuex';

  const { state, getters, commit } = useStore();

  const tag = computed(() => state.modal.data?.tag);
  const folder = computed(() => state.modal.data?.folder);

  const title = computed(() => (folder.value ? `Edit ${folder.value.name}` : 'Create new folder'));

  const name = ref(folder.value?.name || '');
  const description = ref(folder.value?.description || '');

  // Color
  const colors = ['#EEEEFF', '#E4F2FE', '#D9F8F5', '#FEE5C7', '#FFE5E8'];
  const selectedColor = ref(folder.value?.color || colors[0]);
  const styleForColor = (color) => ({
      main: { 'background-color': color },
      light: {
        'background-color': color,
        filter: 'saturate(0.8) brightness(1.03)',
      },
    });

  // Tags
  const tagsSelection = ref((() => {
    // Initialize the selection based on whatever was provided. Editing an existing folder? Use its tags.
    // Creating a new folder from a specific tag's dropdown? Use it as the prepopulated default.
    if (folder.value) {
      return getters['onboardingTagsOverview/tagsForFolder'](folder.value.id);
    } else if (tag.value) {
      return [tag.value];
    }
    return [];
  })());
  const availableTags = computed(() => getters['onboardingTagsOverview/editableTags']);

  // Submission
  const saveDisabled = computed(() => {
    const effectiveName = name.value.trim();
    if (!effectiveName) return true; // Disabled if there's no name
    if (tagsSelection.value.length < 1) return true; // Disabled if there's no tags
    const { allFolders } = state.onboardingTagsOverview;
    if (allFolders.filter(f => f.id !== folder.value?.id).map(f => f.name).includes(effectiveName)) return true; // Disabled if there's another folder with the same name.
    return false;
  });
  const saveArgs = computed(() => ({
      id: folder.value?.id,
      name: name.value.trim(),
      description: description.value.trim(),
      color: selectedColor.value,
      tag_ids: tagsSelection.value.map(t => t.id),
    }));

  // Deletion
  const openDeletionModal = () => {
    if (!folder.value) return;

    window.donut.track('Journey Folders - Deletion Modal Opened', { folderId: folder.value.id });
    commit('updateModal', {
      visible: true,
      subcomponent: 'onboarding-tags-modals-folder-delete',
      data: { folder: folder.value },
      cancelMutation: 'updateModal',
      cancelArgs: state.modal,
    });
  };
</script>

<style scoped lang="scss">
  @import '../../../../styles/donut';

  .color-selector {
    position: relative;
    width: 32px;
    height: 32px;
    border-radius: 32px;
    border: 1px solid $white;
    cursor: pointer;

    .color-selector-interior {
      position: absolute;
      top: 8px;
      left: 8px;
      width: 16px;
      height: 16px;
      border-radius: 16px;
    }
  }
  .color-selector.selected {
    transform: translate(0, -2px);
    box-shadow: -2px 2px 4px $border;
    border: 1px solid $purple;
  }
</style>
